import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import api from '../api/api';
import { reportPreferenceRecovery, reportPreferenceRecoveryMessage } from '../utils/reportPreferenceRecovery';
import { extractApiErrorMessage } from '../utils/apiError';
import { buildRestaurantEmployeePrintHtml, PayrollHistoryItem } from '../utils/restaurantEmployeePrint';
import { printHtmlInHiddenFrame } from '../utils/printDocument';
import { currentRestaurantMonth } from '../utils/restaurantMonth';
import { attendanceMonthRange } from './RestaurantAttendanceHistory';
import { loadRestaurantAttendanceReport } from '../utils/restaurantAttendanceReport';
import RestaurantReportPreview from './RestaurantReportPreview';
import { EmployeeReportTemplate, hasRestaurantAttendanceFields, hasRestaurantPayrollSnapshotFields, restaurantReportFields } from '../utils/restaurantReportFields';
import PersonalReportDeliveryPanel from './PersonalReportDeliveryPanel';

const reportFields = [
  ['position', 'Посада'],
  ['attendance', 'Дні з відмітками'],
  ['attendance-intervals', 'Інтервали приходу й виходу'],
  ['attendance-hours', 'Години табеля та знімка зарплати'],
  ['attendance-coefficients', 'Коефіцієнти табеля та зарплати'],
  ['attendance-notes', 'Примітки табеля'],
  ['payroll', 'Дані зарплати'],
  ['payroll-breakdown', 'Складові зарплати'],
  ['payroll-method', 'Метод нарахування'],
  ['payroll-rates', 'Особисті ставки та частка виторгу'],
  ['payroll-revenue', 'Виторг ресторану у знімку'],
  ['payroll-bonuses', 'Премії (суми й відсотки)'],
  ['payroll-penalties', 'Штрафи й утримання (суми й відсотки)'],
  ['adjustments', 'Коригування після закриття'],
] as const;
type ReportField = typeof reportFields[number][0];
type ReportPreferences = { fields: ReportField[]; version: number };
const defaultReportFields = (template: EmployeeReportTemplate): ReportField[] =>
  reportFields.map(([key]) => key).filter(key => restaurantReportFields[template].defaults.includes(key));

const RestaurantEmployeePrintControl: React.FC<{
  employee: { id: number; operatingGroupId: number; fullName: string; position: string };
  canViewSalary: boolean;
  canViewBusinessRevenue: boolean;
  disabled: boolean;
}> = ({ employee, canViewSalary, canViewBusinessRevenue, disabled }) => {
  const [template, setTemplate] = useState<EmployeeReportTemplate>('employee-statement');
  const [month, setMonth] = useState(currentRestaurantMonth);
  const [showTimesheetDelivery, setShowTimesheetDelivery] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  const [preferences, setPreferences] = useState<ReportPreferences>({ fields: defaultReportFields(template), version: 0 });
  const [preferencesReady, setPreferencesReady] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [savingPreferences, setSavingPreferences] = useState(false);
  const [notice, setNotice] = useState('');
  const [preview, setPreview] = useState<{ html: string; context: string; generation: number } | null>(null);
  const request = useRef<AbortController | null>(null);
  const frameRequest = useRef<AbortController | null>(null);
  const generation = useRef(0);
  const context = JSON.stringify([employee.id, employee.operatingGroupId, month, template,
    canViewSalary, canViewBusinessRevenue, disabled, preferences.fields]);
  const previewHtml = preview?.context === context && preview.generation === generation.current
    ? preview.html : null;
  const cancel = useCallback(() => {
    generation.current++;
    request.current?.abort();
    frameRequest.current?.abort();
    request.current = null;
  }, []);
  useLayoutEffect(() => {
    cancel();
    setWorking(false);
    setSavingPreferences(false);
    setError('');
    setNotice('');
    setPreview(null);
    return cancel;
  }, [context, cancel]);

  useEffect(() => {
    const controller = new AbortController();
    setPreferencesReady(false);
    setRecovering(false);
    api.get<ReportPreferences>(`/restaurant-personnel/report-preferences/${template}`, {
      signal: controller.signal,
    }).then(response => {
      if (controller.signal.aborted) return;
      setPreferences(response.data);
      setPreferencesReady(true);
    }).catch(failure => {
      if (!controller.signal.aborted) {
        const recovery = reportPreferenceRecovery(failure, template, 'print');
        if (recovery) {
          setPreferences({ fields: [], version: recovery.version });
          setRecovering(true);
          setPreferencesReady(true);
          return;
        }
        setError(extractApiErrorMessage(failure, 'Не вдалося завантажити збережені поля звіту.'));
      }
    });
    return () => controller.abort();
  }, [employee.id, employee.operatingGroupId, template]);

  const savePreferences = async () => {
    if (!preferencesReady || savingPreferences || working) return;
    const saveGeneration = generation.current;
    setSavingPreferences(true);
    setError('');
    setNotice('');
    try {
      const response = await api.put<ReportPreferences>(
        `/restaurant-personnel/report-preferences/${template}`, preferences);
      if (generation.current !== saveGeneration) return;
      setPreferences(response.data);
      setRecovering(false);
      setNotice('Поля звіту збережено для вашого облікового запису на всіх пристроях.');
    } catch (failure) {
      if (generation.current === saveGeneration)
        setError(extractApiErrorMessage(failure, 'Не вдалося зберегти поля звіту. Оновіть їх після конфлікту.'));
    } finally {
      if (generation.current === saveGeneration) setSavingPreferences(false);
    }
  };

  const print = async (previewOnly = false) => {
    if (disabled || recovering || !preferencesReady || request.current) return;
    const controller = new AbortController();
    const printGeneration = ++generation.current;
    request.current = controller;
    const valid = () => !controller.signal.aborted && request.current === controller &&
      generation.current === printGeneration;
    setWorking(true);
    setError('');
    const loadAttendance = async () => {
      if (!hasRestaurantAttendanceFields(preferences.fields)) return [];
      return loadRestaurantAttendanceReport(`/restaurant-personnel/${employee.id}/attendance`,
        employee.id, attendanceMonthRange(month), controller.signal, valid);
    };
    const loadSalary = async () => {
      if (template === 'employee-timesheet' || !canViewSalary || !hasRestaurantPayrollSnapshotFields(preferences.fields))
        return { history: null, error: '' };
      try {
        const response = await api.get<PayrollHistoryItem[]>(
          `/restaurant-payroll/employments/${employee.id}/history`, { signal: controller.signal },
        );
        return { history: response.data, error: '' };
      } catch (failure) {
        return { history: null, error: extractApiErrorMessage(failure, 'Не вдалося завантажити історію зарплати ресторану.') };
      }
    };
    try {
      const [attendance, salary, detail] = await Promise.all([
        loadAttendance(),
        loadSalary(),
        api.get<{ id: number; fullName: string; position: string }>(
          `/restaurant-personnel/${employee.id}`, { signal: controller.signal },
        ),
      ]);
      if (!valid()) return;
      if (detail.data.id !== employee.id)
        throw new Error('Отримано дані іншого працівника. Повторіть друк.');
      if (salary.error) setError(salary.error);
      const html = buildRestaurantEmployeePrintHtml({
        template, month, employee: { fullName: detail.data.fullName, position: detail.data.position, attendance }, canViewSalary,
        canViewBusinessRevenue,
        payrollHistory: salary.history, payrollHistoryLoading: false, payrollHistoryError: salary.error,
        fields: preferences.fields,
      });
      if (previewOnly) { setPreview({ html, context, generation: printGeneration }); return; }
      frameRequest.current?.abort();
      frameRequest.current = controller;
      printHtmlInHiddenFrame(html, { signal: controller.signal, onError: failure => {
        if (generation.current === printGeneration)
          setError(extractApiErrorMessage(failure, 'Не вдалося підготувати бланк до друку.'));
      } });
    } catch (failure) {
      if (valid()) setError(extractApiErrorMessage(failure, 'Не вдалося завантажити дані для друку бланка.'));
    } finally {
      if (request.current === controller) {
        if (frameRequest.current !== controller) controller.abort();
        request.current = null;
        setWorking(false);
      }
    }
  };

  return <section className="restaurant-personnel__print" aria-label="Друк бланка працівника" aria-busy={working}>
    {recovering && <p role="alert">{reportPreferenceRecoveryMessage}</p>}
    <div className="restaurant-personnel__actions">
      <label>Шаблон особистого звіту<select value={template} disabled={disabled || working || savingPreferences}
        onChange={event => {
          const next = event.target.value;
          if (next === 'employee-statement' || next === 'employee-timesheet') {
            cancel();
            setPreferencesReady(false);
            setTemplate(next);
            if (next !== 'employee-timesheet') setShowTimesheetDelivery(false);
          }
        }}>
        <option value="employee-statement">Зарплатний звіт працівника</option>
        <option value="employee-timesheet">Особистий місячний табель працівника</option>
      </select></label>
      <label>Місяць бланка<input type="month" required min="2000-01" max="2200-12"
        value={month} disabled={disabled || working} onChange={event => {
          if (/^\d{4}-(0[1-9]|1[0-2])$/.test(event.target.value)) {
            cancel();
            setMonth(event.target.value);
          }
        }} /></label>
      <button type="button" disabled={disabled || recovering || working || !preferencesReady || savingPreferences} onClick={() => void print()}>
        Друк бланка працівника / PDF за місяць
      </button>
      <button type="button" disabled={disabled || working || savingPreferences || !preferencesReady}
        onClick={() => void savePreferences()}>Зберегти поля звіту</button>
      <button type="button" disabled={disabled || working || savingPreferences || !preferencesReady}
        onClick={() => setPreferences(current => ({ ...current, fields: defaultReportFields(template) }))}>Типові поля</button>
      <button type="button" disabled={disabled || recovering || working || savingPreferences || !preferencesReady}
        onClick={() => void print(true)}>Попередній перегляд</button>
    </div>
    {template === 'employee-timesheet' && <>
      <button type="button" disabled={disabled || recovering || working || savingPreferences || !preferencesReady}
        aria-expanded={showTimesheetDelivery}
        onClick={() => setShowTimesheetDelivery(value => !value)}>
        {showTimesheetDelivery ? 'Приховати приватне надсилання табеля' : 'Приватне надсилання табеля'}
      </button>
      {showTimesheetDelivery && <PersonalReportDeliveryPanel
        reportBlocked={disabled || !preferencesReady || recovering}
        template="employee-timesheet"
        year={Number(month.slice(0, 4))}
        month={Number(month.slice(5, 7))}
        employmentId={employee.id}
        scopeKey={String(employee.operatingGroupId)}
      />}
    </>}
    <fieldset disabled={disabled || working || savingPreferences || !preferencesReady}>
      <legend>Поля бланка (вибір облікового запису)</legend>
      {reportFields.filter(([key]) => restaurantReportFields[template].allowed.includes(key)).map(([key, label]) => {
        const salaryField = key.startsWith('payroll') || key === 'adjustments';
        return <label key={key}>
          <input type="checkbox" checked={preferences.fields.includes(key)}
            disabled={(salaryField && !canViewSalary && key !== 'payroll-revenue') ||
              (key === 'payroll-revenue' && !canViewBusinessRevenue && !preferences.fields.includes(key))}
            onChange={event => setPreferences(current => ({
              ...current,
              fields: event.target.checked
                ? [...current.fields, key]
                : current.fields.filter(field => field !== key),
            }))} />
          {(template === 'employee-timesheet' || !canViewSalary) && key === 'attendance-hours' ? 'Години табеля'
            : (template === 'employee-timesheet' || !canViewSalary) && key === 'attendance-coefficients' ? 'Коефіцієнти табеля' : label}
        </label>;
      })}
    </fieldset>
    {working && <p role="status">Підготовка бланка: завантаження всіх інтервалів за місяць…</p>}
    {error && <p role="alert">{error}</p>}
    {notice && <p role="status">{notice}</p>}
    <RestaurantReportPreview html={previewHtml} title="Перегляд бланка працівника" onClose={() => setPreview(null)} />
    <p>{template === 'employee-timesheet' ? 'Повний особистий табель за вибраний місяць, без зарплатних даних.'
      : 'Табель і деталізація затвердженої зарплати за вибраний місяць.'}
      {template === 'employee-statement' && !canViewSalary && ' Зарплатні дані недоступні без окремого дозволу.'}</p>
  </section>;
};

export default RestaurantEmployeePrintControl;
